// components/products/sections/CertificationSection.jsx - FIXED
import React from "react";
import { Plus, Trash2, FileText } from "lucide-react";

const CertificationSection = ({
  formData,
  addCertification,
  updateCertification,
  removeCertification,
  CERTIFICATION_TYPE_ENUM,
  CERTIFICATION_AGENCY_ENUM,
  errors,
}) => {
  // Helper function to format date for input field
  const formatDateForInput = (date) => {
    if (!date) return "";

    // If it's already a string in ISO format, extract the date part
    if (typeof date === "string") {
      return date.split("T")[0];
    }

    // If it's a Date object, convert to ISO string and extract date part
    if (date instanceof Date) {
      return date.toISOString().split("T")[0];
    }

    // Try to create a Date object and format it
    try {
      const dateObj = new Date(date);
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toISOString().split("T")[0];
      }
    } catch (e) {
      console.error("Invalid date:", date);
    }

    return "";
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Product Certifications
        </h3>
        <button
          type="button"
          onClick={addCertification}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Certification
        </button>
      </div>

      {formData.certifications.length > 0 ? (
        <div className="space-y-4">
          {formData.certifications.map((cert, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center">
                  <FileText className="w-5 h-5 text-gray-400 mr-2" />
                  <h4 className="font-medium text-gray-900">
                    Certification #{index + 1}
                  </h4>
                </div>
                <button
                  type="button"
                  onClick={() => removeCertification(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certification Type *
                  </label>
                  <select
                    value={cert.type || ""}
                    onChange={(e) =>
                      updateCertification(index, "type", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Type</option>
                    {CERTIFICATION_TYPE_ENUM.map((type) => (
                      <option key={type} value={type}>
                        {type.replace(/_/g, " ")}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Certificate Number *
                  </label>
                  <input
                    type="text"
                    value={cert.number || ""}
                    onChange={(e) =>
                      updateCertification(index, "number", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Certificate number"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issued By *
                  </label>
                  <select
                    value={cert.issuedBy || ""}
                    onChange={(e) =>
                      updateCertification(index, "issuedBy", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Select Agency</option>
                    {CERTIFICATION_AGENCY_ENUM.map((agency) => (
                      <option key={agency} value={agency}>
                        {agency}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Issued Date
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(cert.issuedDate)}
                    onChange={(e) =>
                      updateCertification(index, "issuedDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Until
                  </label>
                  <input
                    type="date"
                    value={formatDateForInput(cert.validUntil)}
                    onChange={(e) =>
                      updateCertification(index, "validUntil", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Document URL
                  </label>
                  <input
                    type="url"
                    value={cert.documentUrl || ""}
                    onChange={(e) =>
                      updateCertification(index, "documentUrl", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://..."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          <p>No certifications added</p>
          <p className="text-sm mt-1">
            Click "Add Certification" to add product certifications
          </p>
        </div>
      )}
    </div>
  );
};

export default CertificationSection;
